import React, { useState } from 'react';
import { useHasPermission } from '../../../../Utils/checkPermissions';
import {
  REPORT_TYPE_LABELS,
  REPORT_TYPE_PERMS,
} from '../../../../Utils/permissionCodes';
import SubmitReportModal from './SubmitReportModal';
import RevokeReportModal from './RevokeReportModal';
import { formatDateDDMMYYYYHHMMSS_UTC } from '../../../../Utils/data&dateTime';

/**
 * Per-row reconciliation status cluster.
 *
 * Props:
 *  - row: the per-student row from the activity-list serializer (must have
 *      `accounts_report`, `library_report`, `required_report_types`,
 *      `submit_report_actionable_for_user`, `student_id`, `full_name`)
 *  - activityId: number
 *  - onChanged: optional callback fired after a successful submit/revoke,
 *      receives `(reportType, reportRow|null)`
 */
export default function ReconciliationStatusCell({ row, activityId, onChanged }) {
  const hasPermission = useHasPermission();
  const [submitModal, setSubmitModal] = useState(null); // 'accounts' | 'library' | null
  const [revokeReport, setRevokeReport] = useState(null); // report obj | null

  if (!row) return null;
  const required = row.required_report_types || [];
  if (required.length === 0) return null;

  const actionable = new Set(row.submit_report_actionable_for_user || []);
  // The student has gone through the issuance flow at least once (a SAAR row
  // exists). Purely informational — never hides controls; a fresh report is
  // still required to enable any re-download.
  const linkSent = !!row.activity_completed;

  const reportFor = (rt) => (rt === 'accounts' ? row.accounts_report : row.library_report);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {required.map((rt) => {
        const r = reportFor(rt);
        const label = REPORT_TYPE_LABELS[rt];

        if (r && r.submitted) {
          return (
            <span
              key={rt}
              title={
                `Submitted by ${r.submitted_by || 'system'} at ` +
                (formatDateDDMMYYYYHHMMSS_UTC(r.submitted_at) || '—')
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: '#e7f9ee',
                color: '#16a34a',
                border: '1px solid #b7eecb',
                borderRadius: 999,
                padding: '2px 10px',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {label} ✓
              {/* REVOKE TEMPORARILY DISABLED
              {hasPermission(REPORT_TYPE_PERMS[rt]) && (
                <button
                  onClick={() => setRevokeReport(r)}
                  style={{
                    marginLeft: 4,
                    background: 'transparent',
                    border: 'none',
                    color: '#d4380d',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 11,
                    textDecoration: 'underline',
                  }}
                  title="Revoke this report"
                >
                  revoke
                </button>
              )}
              */}
            </span>
          );
        }

        if (actionable.has(rt)) {
          return (
            <button
              key={rt}
              onClick={() => setSubmitModal(rt)}
              style={{
                background: '#7F56DA',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Submit {label} Report
            </button>
          );
        }

        return (
          <span
            key={rt}
            title="Awaiting reconciliation team — you don't have permission to submit this report type"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 999,
              padding: '4px 12px',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {label} report pending
          </span>
        );
      })}

      {linkSent && (
        <span
          title="Admit card link has been sent before — a fresh report is still required for any re-download."
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: '#eef2ff',
            color: '#3730a3',
            border: '1px solid #c7d2fe',
            borderRadius: 999,
            padding: '2px 10px',
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          Link Sent
        </span>
      )}

      <SubmitReportModal
        isOpen={!!submitModal}
        onClose={() => setSubmitModal(null)}
        studentId={row.student_id}
        studentName={row.full_name}
        activityId={activityId}
        reportType={submitModal}
        onSuccess={(reportRow) => onChanged?.(submitModal, reportRow || null)}
      />
      <RevokeReportModal
        isOpen={!!revokeReport}
        onClose={() => setRevokeReport(null)}
        report={revokeReport}
        studentName={row.full_name}
        onSuccess={() => onChanged?.(revokeReport?.report_type, null)}
      />
    </div>
  );
}
